// F-068 : accès délégué à un tiers (locataire ou bailleur), invitation, espace du délégué, révocation.
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { PublicShell } from "@core/layout";
import { useSession } from "@core/session";
import { Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Notice, Table, Tabs, fieldError } from "@core/ui";
import { CATEGORIES, DELEGATION_STATUS, INCIDENT_STATUS, Status, Timeline, addDays, delegApi, meLease, orgApi, todayIso, useAction } from "./shared";
import { Thread } from "./messages";
import { IncidentForm } from "./incidents";

type Delegation = {
  id: string;
  lease_id: string;
  on_behalf: string;
  grantor_label: string;
  delegate_email: string;
  delegate_name: string;
  scopes: string[];
  scope_labels: string[];
  starts_on: string;
  ends_on: string;
  status: string;
  property_label: string;
  revoke_reason: string;
};

function DelegationForm({ path, onDone }: { path: string; onDone: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", messages: false, incidents: false, ends_on: addDays(todayIso(), 90) });
  const act = useAction();
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const scopes = [form.messages ? "messages" : "", form.incidents ? "incidents" : ""].filter(Boolean);
        const r = await act.run(() => post(path, { name: form.name, email: form.email, scopes, ends_on: form.ends_on }), "Invitation envoyée.");
        if (r) {
          setForm({ ...form, name: "", email: "" });
          onDone();
        }
      }}
    >
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Input label="Nom de la personne" required value={form.name} error={fieldError(act.error, "name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input label="Adresse e-mail" type="email" required value={form.email} error={fieldError(act.error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Fin de l’accès" type="date" required value={form.ends_on} error={fieldError(act.error, "ends_on")} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
      </div>
      <fieldset className="stack-sm">
        <legend>Droits accordés</legend>
        <Checkbox label="Consulter les échanges et incidents" checked onChange={() => {}} help="Toujours inclus." />
        <Checkbox label="Écrire des messages" checked={form.messages} onChange={(v) => setForm({ ...form, messages: v })} />
        <Checkbox label="Signaler et suivre les incidents" checked={form.incidents} onChange={(v) => setForm({ ...form, incidents: v })} />
      </fieldset>
      <p className="small muted">La personne se connecte avec son propre compte, à l’adresse indiquée. La signature d’actes n’est jamais déléguée. Vous pouvez révoquer l’accès à tout moment.</p>
      <div className="row">
        <Button type="submit" busy={act.busy}>
          Inviter
        </Button>
      </div>
    </form>
  );
}

function DelegationTable({ items, onRevoke }: { items: Delegation[]; onRevoke?: (d: Delegation) => void }) {
  if (!items.length) return <p className="muted">Aucun accès délégué.</p>;
  return (
    <Table caption="Accès délégués">
      <thead>
        <tr>
          <th>Personne</th>
          <th>Pour le compte de</th>
          <th>Droits</th>
          <th>Période</th>
          <th>Statut</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              {d.delegate_name}
              <span className="block small muted">{d.delegate_email}</span>
            </td>
            <td>{d.on_behalf === "locataire" ? `Locataire (${d.grantor_label})` : d.grantor_label}</td>
            <td className="small">{d.scope_labels.join(", ")}</td>
            <td className="small">
              {date(d.starts_on)} → {date(d.ends_on)}
            </td>
            <td>
              <Status value={d.status} labels={DELEGATION_STATUS} />
            </td>
            <td>
              {onRevoke && ["invitee", "active", "a_venir"].includes(d.status) ? (
                <Button small variant="danger" onClick={() => onRevoke(d)}>
                  Révoquer
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Carte « Accès délégués » dans la location du locataire. */
export function TenantDelegations({ leaseId }: { leaseId: string }) {
  const path = meLease(leaseId, "/delegations");
  const q = useGet<{ items: Delegation[] }>(path);
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const act = useAction();
  return (
    <Card title="Accès délégués" actions={<Button small variant="secondaire" onClick={() => setOpen(!open)}>{open ? "Fermer" : "Donner un accès"}</Button>}>
      <p className="muted small">Un proche peut suivre votre location avec son propre compte, pour une durée limitée et avec les droits que vous choisissez.</p>
      <ErrorBox error={act.error} />
      {open ? <DelegationForm path={path} onDone={() => { setOpen(false); invalidate(path); }} /> : null}
      {q.isLoading ? (
        <Loading />
      ) : (
        <DelegationTable
          items={q.data?.items ?? []}
          onRevoke={async (d) => {
            if (!confirm(`Révoquer l’accès de ${d.delegate_name} ?`)) return;
            await act.run(() => post(meLease(leaseId, `/delegations/${d.id}/revoke`), { reason: "" }), "Accès révoqué.");
            invalidate(path);
          }}
        />
      )}
    </Card>
  );
}

/** Carte « Accès délégués » sur le bail, côté bailleur : toutes les délégations (transparence), création et révocation des siennes. */
export function LandlordDelegations({ orgId, leaseId, canManage }: { orgId: string; leaseId: string; canManage: boolean }) {
  const path = orgApi(orgId, `/leases/${leaseId}/delegations`);
  const q = useGet<{ items: Delegation[] }>(path);
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const act = useAction();
  return (
    <Card title="Accès délégués" actions={canManage ? <Button small variant="secondaire" onClick={() => setOpen(!open)}>{open ? "Fermer" : "Déléguer un accès"}</Button> : null}>
      <p className="muted small">Accès limités à ce bail, accordés par vous ou par les locataires. Les délégations des locataires ne peuvent être révoquées que par eux.</p>
      <ErrorBox error={act.error} />
      {open ? <DelegationForm path={path} onDone={() => { setOpen(false); invalidate(path); }} /> : null}
      {q.isLoading ? (
        <Loading />
      ) : (
        <DelegationTable
          items={q.data?.items ?? []}
          onRevoke={
            canManage
              ? async (d) => {
                  if (d.on_behalf !== "bailleur") return act.setError(new Error("Cette délégation a été accordée par un locataire : lui seul peut la révoquer."));
                  if (!confirm(`Révoquer l’accès de ${d.delegate_name} ?`)) return;
                  await act.run(() => post(orgApi(orgId, `/delegations/${d.id}/revoke`), { reason: "" }), "Accès révoqué.");
                  invalidate(path);
                }
              : undefined
          }
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pages du compte

export function AccountDelegationsPage() {
  const q = useGet<{ received: Delegation[]; granted: Delegation[] }>("/api/v1/me/vie/delegations");
  const invalidate = useInvalidate();
  const act = useAction();
  return (
    <PublicShell wide>
      <p className="small">
        <Link to="/moi">← Mon espace personnel</Link>
      </p>
      <h1>Accès délégués</h1>
      <ErrorBox error={act.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="stack">
          <Card title="Accès que l’on m’a confiés">
            {q.data?.received.length ? (
              <ul className="vie-list">
                {q.data.received.map((d) => (
                  <li key={d.id}>
                    {d.status === "active" ? (
                      <Link className="vie-list-item" to={`/moi/delegations/${d.id}`}>
                        <strong>{d.property_label}</strong>
                        <span className="small muted">
                          Pour le compte de {d.grantor_label} · jusqu’au {date(d.ends_on)}
                        </span>
                      </Link>
                    ) : (
                      <div className="vie-list-item">
                        <strong>{d.property_label}</strong>
                        <Status value={d.status} labels={DELEGATION_STATUS} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="Aucun accès délégué">Lorsqu’un locataire ou un bailleur vous invite, ouvrez le lien reçu par courriel.</Empty>
            )}
          </Card>
          <Card title="Accès que j’ai accordés">
            <DelegationTable
              items={q.data?.granted ?? []}
              onRevoke={async (d) => {
                if (!confirm(`Révoquer l’accès de ${d.delegate_name} ?`)) return;
                await act.run(() => post(meLease(d.lease_id, `/delegations/${d.id}/revoke`), { reason: "" }), "Accès révoqué.");
                invalidate("/api/v1/me/vie/delegations");
              }}
            />
          </Card>
        </div>
      )}
    </PublicShell>
  );
}

export function DelegationInvitationPage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const { user } = useSession();
  const q = useGet<any>(`/api/v1/vie/delegation-invitations/${token}`);
  const act = useAction();
  return (
    <PublicShell>
      <h1>Accès délégué</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? act.error} />
      {q.data ? (
        <div className="stack">
          <p>
            {q.data.grantor_label} vous propose un accès délégué{q.data.property_label ? ` pour le logement « ${q.data.property_label} »` : ""}, du {date(q.data.starts_on)} au {date(q.data.ends_on)}.
          </p>
          <ul>
            {q.data.scopes.map((s: any) => (
              <li key={s.value}>{s.label}</li>
            ))}
          </ul>
          {q.data.email_matches ? (
            <Button
              busy={act.busy}
              onClick={async () => {
                const r = await act.run(() => post<{ id: string }>(`/api/v1/vie/delegation-invitations/${token}/accept`, {}), "Accès accepté.");
                if (r) nav(`/moi/delegations/${r.id}`);
              }}
            >
              Accepter l’accès
            </Button>
          ) : (
            <Notice tone="attention">
              Cette invitation est destinée à {q.data.delegate_email}. Vous êtes connecté avec {user?.email}. Connectez-vous avec l’adresse invitée : un accès délégué ne se partage pas.
            </Notice>
          )}
        </div>
      ) : null}
    </PublicShell>
  );
}

/** Espace du délégué : messages et incidents du bail, dans le périmètre accordé. */
export function DelegationWorkspacePage() {
  const { delegationId = "" } = useParams();
  const q = useGet<any>(delegApi(delegationId));
  const [tab, setTab] = useState<"messages" | "incidents">("messages");
  const [conv, setConv] = useState("");
  const [incident, setIncident] = useState("");
  const convs = useGet<any>(q.data ? delegApi(delegationId, "/conversations") : null);
  const incidents = useGet<any>(q.data && tab === "incidents" ? delegApi(delegationId, "/incidents") : null);
  const invalidate = useInvalidate();
  const act = useAction();
  const [newSubject, setNewSubject] = useState({ subject: "", body: "" });
  const scopes: string[] = q.data?.delegation?.scopes ?? [];
  return (
    <PublicShell wide>
      <p className="small">
        <Link to="/moi/delegations">← Mes accès délégués</Link>
      </p>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="stack">
          <h1>{q.data.lease.property_label}</h1>
          <Notice tone="info" title={`Pour le compte de ${q.data.delegation.grantor_label}`}>
            {q.data.rappel} Accès jusqu’au {date(q.data.delegation.ends_on)}.
          </Notice>
          <Tabs value={tab} onChange={setTab} items={[{ value: "messages", label: "Messages" }, { value: "incidents", label: "Incidents" }]} />
          {tab === "messages" ? (
            <div className="stack">
              {scopes.includes("messages") ? (
                <Card title="Nouveau message">
                  <form
                    className="stack-sm"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const r = await act.run(() => post<{ id: string }>(delegApi(delegationId, "/conversations"), newSubject), "Message envoyé.");
                      if (r) {
                        setNewSubject({ subject: "", body: "" });
                        setConv(r.id);
                        invalidate(delegApi(delegationId, "/conversations"));
                      }
                    }}
                  >
                    <ErrorBox error={act.error} />
                    <Input label="Objet" required value={newSubject.subject} onChange={(e) => setNewSubject({ ...newSubject, subject: e.target.value })} />
                    <Input label="Message" required value={newSubject.body} onChange={(e) => setNewSubject({ ...newSubject, body: e.target.value })} />
                    <Button type="submit" busy={act.busy}>
                      Envoyer
                    </Button>
                  </form>
                </Card>
              ) : null}
              <div className="vie-split">
                <ul className="vie-list">
                  {(convs.data?.items ?? []).map((c: any) => (
                    <li key={c.id}>
                      <button type="button" className="vie-list-item" aria-current={conv === c.id ? "true" : undefined} onClick={() => setConv(c.id)}>
                        <strong>{c.subject}</strong>
                        <span className="small muted">{c.last_message_at ? dateTime(c.last_message_at) : ""}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {conv ? (
                  <Card>
                    <Thread key={conv} getPath={delegApi(delegationId, `/conversations/${conv}`)} postPath={delegApi(delegationId, `/conversations/${conv}/messages`)} canWrite={scopes.includes("messages")} />
                  </Card>
                ) : (
                  <p className="muted">Choisissez une conversation.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="stack">
              {scopes.includes("incidents") && q.data.on_behalf === "locataire" ? (
                <Card title="Signaler un incident">
                  <IncidentForm path={delegApi(delegationId, "/incidents")} onCreated={() => invalidate(delegApi(delegationId, "/incidents"))} />
                </Card>
              ) : null}
              {incidents.isLoading ? <Loading /> : null}
              <ul className="vie-list">
                {(incidents.data?.items ?? []).map((i: any) => (
                  <li key={i.id}>
                    <button type="button" className="vie-list-item" aria-current={incident === i.id ? "true" : undefined} onClick={() => setIncident(i.id)}>
                      <span className="row-between">
                        <strong>{i.title}</strong>
                        <Status value={i.status} labels={INCIDENT_STATUS} />
                      </span>
                      <span className="small muted">
                        {CATEGORIES[i.category]} · {dateTime(i.created_at)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {incident ? <DelegateIncident delegationId={delegationId} incidentId={incident} canComment={scopes.includes("incidents")} /> : null}
            </div>
          )}
        </div>
      ) : null}
    </PublicShell>
  );
}

function DelegateIncident({ delegationId, incidentId, canComment }: { delegationId: string; incidentId: string; canComment: boolean }) {
  const path = delegApi(delegationId, `/incidents/${incidentId}`);
  const q = useGet<any>(path);
  const [body, setBody] = useState("");
  const act = useAction();
  const invalidate = useInvalidate();
  if (!q.data) return q.isLoading ? <Loading /> : <ErrorBox error={q.error} />;
  return (
    <Card title={q.data.incident.title}>
      <Timeline items={q.data.events.map((e: any) => ({ id: e.id, at: e.created_at, who: e.author_label, text: e.body }))} />
      {canComment ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await act.run(() => post(`${path}/comments`, { body }), "Commentaire ajouté.")) {
              setBody("");
              invalidate(path);
            }
          }}
        >
          <ErrorBox error={act.error} />
          <Input label="Ajouter un commentaire" value={body} onChange={(e) => setBody(e.target.value)} />
          <Button type="submit" small busy={act.busy}>
            Envoyer
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
